import React, { useState } from 'react';
import {
  Badge,
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  Popover,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import NotificationsIcon from '@mui/icons-material/Notifications';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import type { InsightNotification, InsightNotificationLink } from '../notificationTypes';
import { NOTIFICATION_CENTER_TEST_IDS } from '../notificationTestIds';

interface NotificationCenterProps {
  notifications: InsightNotification[];
  unreadCount: number;
  error?: string | null;
  markRead: (notificationId: string) => Promise<void> | void;
  markAllRead: () => Promise<void> | void;
  /** Resolves a notification link to an app route; enables the "Open" action. */
  resolveLink?: (link: InsightNotificationLink) => string | null;
  onNavigate?: (path: string) => void;
}

const SEVERITY_COLOR: Record<InsightNotification['severityLabel'], 'error' | 'warning' | 'info'> = {
  critical: 'error',
  warning: 'warning',
  info: 'info',
};

export const NotificationCenter: React.FC<NotificationCenterProps> = ({
  notifications,
  unreadCount,
  error,
  markRead,
  markAllRead,
  resolveLink,
  onNavigate,
}) => {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const open = Boolean(anchor);

  return (
    <>
      <Tooltip title="Insight notifications">
        <IconButton
          size="small"
          aria-label="open insight notifications"
          data-testid={NOTIFICATION_CENTER_TEST_IDS.bell}
          onClick={(event) => setAnchor(event.currentTarget)}
        >
          <Badge
            color="error"
            badgeContent={unreadCount}
            data-testid={NOTIFICATION_CENTER_TEST_IDS.badge}
          >
            <NotificationsIcon fontSize="small" />
          </Badge>
        </IconButton>
      </Tooltip>
      <Popover
        open={open}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Box sx={{ width: 420, maxHeight: 480, overflow: 'auto', p: 1.5 }} data-testid={NOTIFICATION_CENTER_TEST_IDS.panel}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
            <Typography variant="subtitle2">Insight notifications</Typography>
            <Button
              size="small"
              startIcon={<DoneAllIcon />}
              disabled={unreadCount === 0}
              data-testid={NOTIFICATION_CENTER_TEST_IDS.markAllRead}
              onClick={markAllRead}
            >
              Mark all read
            </Button>
          </Stack>
          <Divider sx={{ mb: 1 }} />

          {error && (
            <Typography variant="body2" color="error" sx={{ mb: 1 }}>{error}</Typography>
          )}

          {notifications.length === 0 ? (
            <Typography
              variant="body2"
              color="text.secondary"
              data-testid={NOTIFICATION_CENTER_TEST_IDS.emptyState}
            >
              All clear — no anomalies or pending decisions.
            </Typography>
          ) : (
            <Stack spacing={1}>
              {notifications.map(notification => {
                const linkPath = notification.link && resolveLink ? resolveLink(notification.link) : null;
                return (
                  <Box
                    key={notification.id}
                    data-testid={NOTIFICATION_CENTER_TEST_IDS.item(notification.id)}
                    sx={{
                      p: 1,
                      borderRadius: 1,
                      border: '1px solid',
                      borderColor: 'divider',
                      bgcolor: notification.status === 'unread' ? 'action.hover' : 'transparent',
                    }}
                  >
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                      <Chip size="small" label={notification.severityLabel} color={SEVERITY_COLOR[notification.severityLabel]} sx={{ height: 18, fontSize: 10 }} />
                      <Chip size="small" label={notification.kind.replace(/_/g, ' ')} sx={{ height: 18, fontSize: 10 }} />
                      {Boolean(notification.occurrenceCount && notification.occurrenceCount > 1) && (
                        <Chip size="small" label={`×${notification.occurrenceCount}`} sx={{ height: 18, fontSize: 10 }} />
                      )}
                      <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
                        {notification.createdAt.slice(0, 16).replace('T', ' ')}
                      </Typography>
                    </Stack>
                    <Typography variant="body2" fontWeight={600}>{notification.title}</Typography>
                    <Typography variant="body2" color="text.secondary">{notification.body}</Typography>
                    {notification.nextAction && (
                      <Typography variant="caption" color="text.secondary">Next: {notification.nextAction}</Typography>
                    )}
                    <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
                      {linkPath && (
                        <Button
                          size="small"
                          data-testid={NOTIFICATION_CENTER_TEST_IDS.itemLink(notification.id)}
                          onClick={() => {
                            void markRead(notification.id);
                            onNavigate?.(linkPath);
                            setAnchor(null);
                          }}
                        >
                          Open
                        </Button>
                      )}
                      {notification.status === 'unread' && (
                        <Button
                          size="small"
                          data-testid={NOTIFICATION_CENTER_TEST_IDS.itemMarkRead(notification.id)}
                          onClick={() => void markRead(notification.id)}
                        >
                          Mark read
                        </Button>
                      )}
                    </Stack>
                  </Box>
                );
              })}
            </Stack>
          )}
        </Box>
      </Popover>
    </>
  );
};
